import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Camera, FileText, Loader2, Sparkles, UploadCloud } from "lucide-react";
import { ocrBill, uploadReceipt } from "../../api/accounting";
import { useApiError } from "../../hooks/useApiError";

// منطقة رفع الفاتورة (صورة/PDF/كاميرا) → تحليل Gemini → onExtracted بحقول جاهزة.
export const BillOcrDropzone = ({ onExtracted, onReceipt, receipt }) => {
  const { t } = useTranslation();
  const describe = useApiError();
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const process = async (file) => {
    if (!file) return;
    setBusy(true);
    // 1) رفع الإيصال أولاً (مطلوب للحفظ) — إن فشل هنا نتوقف.
    let uploaded = null;
    try {
      uploaded = await uploadReceipt(file);
      onReceipt({ attachment_url: uploaded.url, receipt_hash: uploaded.receipt_hash, name: uploaded.original_filename });
    } catch (error) {
      toast.error(describe(error));
      setBusy(false);
      return;
    }
    // 2) تحليل الفاتورة — إن فشل نتابع بالحقول اليدوية بلا كسر تجربة المستخدم.
    try {
      const parsed = await ocrBill(file);
      if (parsed && !parsed.error) {
        onExtracted(parsed);
        toast.success(t("acct.bills.ocr.done"));
      } else {
        toast(t("acct.bills.ocr.softFail"));
      }
    } catch (error) {
      toast(t("acct.bills.ocr.softFail"));
    } finally { setBusy(false); }
  };

  const pick = (e) => { const f = e.target.files?.[0]; process(f); e.target.value = ""; };
  const drop = (e) => { e.preventDefault(); setDragOver(false); process(e.dataTransfer.files?.[0]); };

  return (
    <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
         onDragLeave={() => setDragOver(false)}
         onDrop={drop} data-testid="bill-ocr-dropzone"
         className={`relative grid place-content-center rounded-3xl border-2 border-dashed p-10 text-center transition
                      ${dragOver ? "border-primary bg-primary/8 scale-[1.01]" : "border-primary/30 bg-background/40"}`}>
      <input ref={fileRef} type="file" hidden accept="image/*,application/pdf" onChange={pick} data-testid="bill-ocr-file" />
      <input ref={camRef}  type="file" hidden accept="image/*" capture="environment" onChange={pick} data-testid="bill-ocr-cam" />
      {busy ? (
        <div className="flex flex-col items-center gap-2 text-primary">
          <Loader2 className="h-10 w-10 animate-spin" />
          <p className="text-sm font-bold">{t("acct.bills.ocr.busy")}</p>
        </div>
      ) : receipt ? (
        <div className="flex flex-col items-center gap-2 text-emerald-600">
          <Sparkles className="h-10 w-10" />
          <p className="text-sm font-bold">{t("acct.bills.ocr.ready")}</p>
          <p className="max-w-xs truncate text-xs text-muted-foreground">{receipt.name || "—"}</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <UploadCloud className="h-10 w-10 text-primary" />
          <p className="text-sm font-bold">{t("acct.bills.ocr.prompt")}</p>
          <p className="text-[11px] text-muted-foreground">{t("acct.bills.ocr.hint")}</p>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            <button type="button" onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-2 rounded-2xl border border-primary/40 bg-primary/8 px-3 py-2 text-xs font-bold text-primary hover:bg-primary/15">
              <FileText className="h-4 w-4" /> {t("acct.bills.ocr.picker")}
            </button>
            <button type="button" onClick={() => camRef.current?.click()}
                    className="flex items-center gap-2 rounded-2xl border border-primary/40 bg-primary/8 px-3 py-2 text-xs font-bold text-primary hover:bg-primary/15">
              <Camera className="h-4 w-4" /> {t("acct.bills.ocr.camera")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
